import { Box, Stack, SxProps, Typography, useTheme } from '@mui/material';
import React, { FC, useCallback, useState } from 'react';
import { useDialog, useOrder } from '@/logic/state';

import { ExperienceModel } from '@/logic/services/experiences';
import { FormattedMessage } from 'react-intl';

import { PATHS } from '@/constants/routes';
import { TimesFilter } from './times-filter';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useClearBooking } from '@/logic/hooks/use-clear-booking';
import { useRouter } from 'next/router';
import { useSeatsSelectionStatus } from '@/logic/hooks';
import { useShowtimesProvider } from '@/hooks/use-showtimes-provider';
import ImageBoxFuture from '../shared/image-box-future';

const EmptyExperienceImgCard = '/icons/cinema/emptyExperienceImgCard.svg';

interface ExperienceCardProps {
  movieId: number;
  experience: ExperienceModel;
  index: number;
  sxProps?: SxProps;
  experienceLogoSx?: SxProps;
  hiddenShowTimes?: boolean;
  slug?: string;
  callOnFilmHasBeenStarted?: () => void;
  dropDownItem?: boolean;
}
export const ExperienceCard: FC<ExperienceCardProps> = ({
  movieId,
  experience,
  index,
  sxProps,
  experienceLogoSx,
  hiddenShowTimes,
  callOnFilmHasBeenStarted,
  dropDownItem,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const [activeTimeTab, setActiveTimeTab] = useState<number | boolean>(false);

  const setDialog = useDialog(s => s.setDialog, shallow);
  const order = useOrder(s => s.order, shallow);
  const { clearBooking } = useClearBooking();

  const { isThereSelectedSeats } = useSeatsSelectionStatus();
  const { handleClickShowtime } = useShowtimesProvider();

  const defaultHandleClick = useCallback(
    (sessionId: number, isAfterMidnight: boolean, isThreeD: boolean) => {
      setActiveTimeTab(
        experience.sessions.findIndex(session => session.id === sessionId)
      );
      if (
        (router.pathname === PATHS.MOVIES_SLUG_BOOKING && isThereSelectedSeats) ||
        (router.pathname === PATHS.BOOKINGS_ID_CHECKOUT && order)
      ) {
        setDialog({
          wrapper: true,
          isCancelSeatsSelectionConfirmDialog: true,
          cancelSeatsSelectionConfirmDialogPayload: {
            redirect: false,
          },
          rulesOfEntryDialogPayload: {
            sessionId,
            isAfterMidnight,
            isThreeD,
            movieId,
          },
        });
      } else {
        clearBooking();
        setDialog({
          wrapper: true,
          isRulesOfEntryDialog: true,
          rulesOfEntryDialogPayload: {
            sessionId,
            isAfterMidnight,
            isThreeD,
            movieId,
            refetchFunction: callOnFilmHasBeenStarted,
          },
        });
      }
    },
    [
      callOnFilmHasBeenStarted,
      clearBooking,
      experience.sessions,
      isThereSelectedSeats,
      movieId,
      order,
      router.pathname,
      setDialog,
    ]
  );

  const handleClick = handleClickShowtime
    ? (sessionId: number, isAfterMidnight: boolean, isThreeD: boolean) =>
        handleClickShowtime({
          sessionId,
          isAfterMidnight,
          movieId,
          isThreeD,
        })
    : defaultHandleClick;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        borderRadius: '20px',
        boxShadow: theme.customShadows.cardShadow,
        border: `1px solid ${theme.palette.lightGrey.main}`,
        marginTop: index === 0 ? '40px' : '24px',
        ...sxProps,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginX: pxToRem(24),
          marginTop: pxToRem(16),
          marginBottom: pxToRem(24),
        }}
      >
        <ImageBoxFuture
          img={experience.logo || EmptyExperienceImgCard}
          title={experience.logoAlt || experience?.name}
          layout='intrinsic'
          objectFit='contain'
          width='794px'
          height='397px'
          sx={{
            minWidth: {
              mobile: '81px',
              desktop: pxToRem(185),
            },
            ...experienceLogoSx,
          }}
        />
        <Stack
          direction='row'
          sx={{
            marginInlineStart: pxToRem(24),
            alignItems: 'flex-end',
            flexWrap: dropDownItem ? { mobile: 'wrap !important' } : {},
          }}
        >
          <Typography
            component='p'
            sx={{
              color: theme.palette.grey[50],
              fontWeight: 700,
              fontSize: pxToRem(16),
              lineHeight: '24px',
              width: '80%',
              WebkitLineClamp: { tablet: '2', mobile: '3' },
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {experience.description}
          </Typography>
          <Typography
            component='a'
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: pxToRem(16),
              lineHeight: '24px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginInlineStart: '8px',
            }}
            onClick={() =>
              setDialog({
                wrapper: true,
                isExperienceInfoDialog: true,
                experienceInfoDialogPayload: experience,
              })
            }
          >
            <FormattedMessage id='global.read_more' />
          </Typography>
        </Stack>
      </Box>
      {!hiddenShowTimes && (
        <Box sx={{ marginBottom: pxToRem(16) }}>
          <TimesFilter
            experienceName={experience?.name}
            sessions={experience.sessions}
            activeTab={activeTimeTab}
            handleClick={handleClick}
          />
        </Box>
      )}
    </Box>
  );
};

export default ExperienceCard;
